import { useCallback, useEffect, useState } from 'react';
import { apiFetch, descargarArchivo } from '../../lib/api';
import { Ayuda } from '../../components/Ayuda';
import { Spinner } from '../../components/Spinner';

interface Campo { codigo: string; grupo: string; campo: string; tipo: string; unidad?: string; ayuda: string; capitulos: string[]; deCapitulo: boolean; obligatorio: boolean }
interface Valor { valor: string | number | null; unidad: string | null; observacion: string | null }
interface Problema { codigo: string | null; tipo: string; mensaje: string; correccion: string }
interface Ficha { version: number; campos: Record<string, Valor>; problemas: Problema[]; completitud_con: number; completitud_total: number; archivo_ruta: string | null; archivo_modified_at: string | null; archivo_por: string | null; importada_at: string | null }
interface Version { version: number; diff: Record<string, { antes: Valor | null; despues: Valor | null }>; control: Record<string, unknown> | null; archivo_modified_at: string | null; archivo_por: string | null; created_at: string }
interface Respuesta { ficha: Ficha | null; problemas_sin_ficha: Problema[]; versiones: Version[]; campos: Campo[] }

export const AYUDA_FICHA = 'La Ficha DIA es la fuente de verdad primaria del proyecto: un Excel estándar que AMBEC deja en la carpeta del proyecto ("Ficha DIA - <proyecto>.xlsx"). La plataforma lo importa al sincronizar, lo valida y lo versiona; los agentes usan estos datos antes que cualquier documento. Se corrige SOLO en el Excel.';
const fecha = (iso: string | null) => (iso ? new Date(iso).toLocaleString('es-CL', { dateStyle: 'short', timeStyle: 'short' }) : '—');
const fmt = (v: Valor | null | undefined) => (v && v.valor !== null && v.valor !== undefined ? `${v.valor}${v.unidad ? ' ' + v.unidad : ''}` : '—');

/** Pestaña Ficha de un proyecto (2026-10-22): campos por grupo, avisos de validación, historial; solo lectura. */
export function FichaProyecto({ favoritoId, nombre, onSincronizar }: { favoritoId: string; nombre: string; onSincronizar: () => Promise<void> }) {
  const [data, setData] = useState<Respuesta | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [sincronizando, setSincronizando] = useState(false);
  const cargar = useCallback(async () => {
    try { setData(await apiFetch<Respuesta>(`/api/dia/${favoritoId}/ficha`)); }
    catch (e) { setError(e instanceof Error ? e.message : 'No se pudo cargar la ficha.'); }
  }, [favoritoId]);
  useEffect(() => { void cargar(); }, [cargar]);
  if (error) return <p role="alert" className="alerta-error">{error}</p>;
  if (!data) return <div className="flex justify-center py-10"><Spinner tamano="md" /></div>;
  const { ficha, versiones, campos, problemas_sin_ficha } = data;
  const nombreArchivo = `Ficha DIA - ${nombre}.xlsx`;
  const plantilla = () => void descargarArchivo(`/api/dia/ficha/plantilla?nombre=${encodeURIComponent(nombre)}`, nombreArchivo);
  const sincronizar = async () => { setSincronizando(true); try { await onSincronizar(); } finally { setSincronizando(false); } };
  const porCodigo = (c: string) => ficha?.problemas.filter((p) => p.codigo === c) ?? [];
  const generales = ficha?.problemas.filter((p) => p.codigo === null || !campos.some((c) => c.codigo === p.codigo)) ?? [];
  const grupos = [...new Set(campos.map((c) => c.grupo))];
  return (
    <section className="flex flex-col gap-4">
      <div className="tarjeta flex flex-wrap items-center justify-between gap-3 p-4">
        <div className="min-w-0">
          <h2 className="flex items-center gap-1.5 text-sm font-medium text-azul-noche">Ficha del proyecto <Ayuda texto={AYUDA_FICHA} /></h2>
          {ficha ? (
            <p className="text-[12px] font-normal text-gris">
              v{ficha.version} · {ficha.completitud_con}/{ficha.completitud_total} campos de capítulo · importada {fecha(ficha.importada_at)} · Excel modificado {fecha(ficha.archivo_modified_at)}{ficha.archivo_por ? ` por ${ficha.archivo_por}` : ''}
            </p>
          ) : (
            <p className="text-[12px] font-normal text-gris">Este proyecto aún no tiene Ficha DIA.</p>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          <Ayuda texto="Excel vacío con los 39 campos, su ayuda y los valores permitidos. Llénalo y déjalo en la carpeta del proyecto con el nombre que trae." toque={false}>
            <button type="button" onClick={plantilla} className="btn-secundario !h-8 !text-[12.5px]">📋 Descargar plantilla</button>
          </Ayuda>
          <Ayuda texto="Vuelve a leer la carpeta de SharePoint (documentos y ficha). Si cambiaste el Excel, en unos minutos aparece aquí la versión nueva." toque={false}>
            <button type="button" onClick={() => void sincronizar()} disabled={sincronizando} className="btn-secundario !h-8 !text-[12.5px] disabled:opacity-50">{sincronizando ? 'Sincronizando…' : '🔄 Sincronizar ahora'}</button>
          </Ayuda>
        </div>
      </div>

      {(generales.length > 0 || problemas_sin_ficha.length > 0) && (
        <div className="rounded-xl bg-amber-50 px-4 py-3 text-[12.5px] text-amber-900 ring-1 ring-inset ring-amber-200">
          <p className="font-medium">⚠ Avisos de la importación (se corrigen en el Excel y se sincroniza):</p>
          <ul className="mt-1 list-inside list-disc">
            {[...generales, ...problemas_sin_ficha].map((p, i) => <li key={i}>{p.mensaje} <span className="text-amber-700/80">→ {p.correccion}</span></li>)}
          </ul>
        </div>
      )}

      {!ficha ? (
        <div className="tarjeta p-6 text-center text-sm font-light leading-relaxed text-gris">
          Descarga la plantilla, complétala y déjala en la carpeta del proyecto como <span className="font-medium text-texto">{nombreArchivo}</span>. Al sincronizar se importa sola.
          <Ayuda texto="Sin ficha, los agentes trabajan solo con los documentos de la carpeta y marcan [FALTA] lo que no encuentran. Con ficha, cada dato base sale de aquí, es trazable y, si cambia, los capítulos afectados avisan." />
        </div>
      ) : (
        <>
          {ficha.problemas.some((p) => p.codigo && campos.some((c) => c.codigo === p.codigo)) && (
            <p className="text-[12px] text-amber-800">⚠ Hay campos con avisos de validación: aparecen marcados en su fila.</p>
          )}
          {grupos.map((g) => (
            <div key={g} className="tarjeta p-4">
              <h3 className="flex items-center gap-1.5 text-[13px] font-medium text-azul-noche">
                {g}<Ayuda texto={`Campos del grupo ${g}. Los que usan los capítulos cuentan para la completitud; los administrativos no.`} />
              </h3>
              <dl className="mt-2 divide-y divide-slate-100">
                {campos.filter((c) => c.grupo === g).map((c) => {
                  const v = ficha.campos[c.codigo];
                  const prob = porCodigo(c.codigo);
                  const vacio = !v || v.valor === null || v.valor === undefined;
                  return (
                    <div key={c.codigo} className="flex flex-col gap-0.5 py-2 sm:flex-row sm:items-start sm:gap-4">
                      <dt className="flex items-center gap-1 text-[12.5px] text-gris sm:w-72 sm:shrink-0">
                        {c.campo}{c.obligatorio && <span className="text-red-500" title="obligatorio">*</span>}
                        <Ayuda texto={`${c.ayuda} Código ${c.codigo}${c.capitulos.length ? ` · lo usan: ${c.capitulos.join(', ')}` : ' · administrativo'}.`} />
                      </dt>
                      <dd className={`min-w-0 flex-1 break-words text-[13px] ${vacio ? 'font-light text-gris' : 'font-normal text-texto'}`}>
                        {fmt(v)}
                        {v?.observacion && <span className="ml-2 text-[11.5px] text-gris">({v.observacion})</span>}
                        {prob.map((p, i) => <span key={i} className="mt-0.5 block text-[11.5px] text-amber-700">⚠ {p.mensaje} → {p.correccion}</span>)}
                      </dd>
                    </div>
                  );
                })}
              </dl>
            </div>
          ))}
          {versiones.length > 0 && (
            <details className="tarjeta p-4">
              <summary className="cursor-pointer text-[13px] font-medium text-azul-profundo">
                Historial ({versiones.length} {versiones.length === 1 ? 'versión' : 'versiones'})
                <Ayuda texto="Cada vez que el Excel cambia en SharePoint se guarda una versión con qué campos cambiaron, quién modificó el archivo y la nota de la hoja Control." />
              </summary>
              <ul className="mt-2 flex flex-col gap-2">
                {versiones.map((v) => (
                  <li key={v.version} className="rounded-xl border border-slate-200 px-3 py-2 text-[12px]">
                    <p className="font-medium text-azul-noche">
                      v{v.version} · {fecha(v.created_at)}{v.archivo_por ? ` · ${v.archivo_por}` : ''}{v.control?.['Qué cambió'] ? ` · "${String(v.control['Qué cambió'])}"` : ''}
                    </p>
                    <ul className="mt-1 list-inside list-disc text-gris">
                      {Object.entries(v.diff).map(([c, d]) => <li key={c}>{campos.find((x) => x.codigo === c)?.campo ?? c}: {fmt(d.antes)} → <span className="text-texto">{fmt(d.despues)}</span></li>)}
                      {Object.keys(v.diff).length === 0 && <li>versión inicial</li>}
                    </ul>
                  </li>
                ))}
              </ul>
            </details>
          )}
        </>
      )}
    </section>
  );
}
